import React, { useState } from 'react';
import { Button } from '@/components/ui';
import type { ShareData } from '../types';
import { generateShareText, generateShareEmoji } from '../logic';

interface ShareButtonProps {
  won: boolean;
  answer: string;
  attempts: number;
  validationGrid: string[][];
  className?: string;
}

const ShareButton: React.FC<ShareButtonProps> = ({
  won,
  answer,
  attempts,
  validationGrid,
  className = '',
}) => {
  const [showCopiedToast, setShowCopiedToast] = useState(false);

  // Better platform detection - check if we're on mobile
  const isMobile = () => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
           (navigator.maxTouchPoints && navigator.maxTouchPoints > 2);
  };

  const handleShare = async () => {
    const shareData: ShareData = {
      question: '', // We don't show the question in share text anymore
      answer,
      attempts,
      won,
      date: new Date().toLocaleDateString(),
    };

    const shareText = generateShareText(shareData);
    const shareEmoji = generateShareEmoji(validationGrid);
    const fullShareText = `${shareText}\n\n${shareEmoji}`;

    if (isMobile() && navigator.share && typeof navigator.share === 'function') {
      // Mobile: Use native share
      try {
        await navigator.share({
          text: fullShareText,
        });
      } catch {
        console.log('Native share failed, falling back to clipboard');
        // Fallback to clipboard if native share fails
        await copyToClipboard(fullShareText);
      }
    } else {
      // Desktop: Always copy to clipboard
      await copyToClipboard(fullShareText);
    }
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setShowCopiedToast(true);
      setTimeout(() => setShowCopiedToast(false), 2000);
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setShowCopiedToast(true);
      setTimeout(() => setShowCopiedToast(false), 2000);
    }
  };

  const isOnMobile = isMobile();

  return (
    <div className="relative">
      <Button
        variant='primary'
        onClick={handleShare}
        className={`flex items-center space-x-2 ${className}`}
      >
        {isOnMobile ? (
          <>
            <span>Share Results</span>
          </>
        ) : (
          <>
            <span>Share Results</span>
          </>
        )}
      </Button>
      
      {/* Toast notification for clipboard copy */}
      {showCopiedToast && (
        <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 px-3 py-1 bg-gray-300 text-black text-sm rounded shadow-lg z-50">
          Copied
        </div>
      )}
    </div>
  );
};

export default ShareButton;
